import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import { signupUser } from "./api/apiFunctions";
import CreateCardModal from "./CreateCardModal";
import DashboardConfirmationModal from "./DashboardConfirmationModal";
import { t } from "./i18n";
import PaymentModal from "./PaymentModal";
import VerificationModal from "./VerificationModal";

export default function SignupPage({ lang = "en" }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
  });
  const [error, setError] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingRole, setPendingRole] = useState("");
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.role) {
      setError(t("signupErrorRequired", lang));
      return;
    }

    if (!termsAccepted) {
      setError(t("signupErrorTerms", lang));
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      // Call the signup API
      const response = await signupUser({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
      });

      // Store the token if it exists in the response
      if (response.token) {
        localStorage.setItem("authToken", response.token);
      }

      // Set pending role and show verification modal first
      setPendingRole(form.role);
      setShowVerificationModal(true);
    } catch (error) {
      console.error("Signup failed:", error);
      setError(
        error.message ||
          t("signupError", lang) ||
          "Signup failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDashboardModalClose = () => {
    setShowDashboardModal(false);
    if (pendingRole === "patient") {
      setShowCreateCardModal(true);
    } else if (pendingRole === "doctor") {
      navigate("/doctor-dashboard");
    }
  };

  const handlePayment = () => {
    setShowPaymentModal(false);
    if (pendingRole === "patient") {
      setShowCreateCardModal(true);
    } else if (pendingRole === "doctor") {
      navigate("/doctor-dashboard");
    }
  };

  const handleVerification = (verificationCode) => {
    if (verificationCode.length === 6) {
      if (pendingRole === "patient") {
        setShowDashboardModal(true);
      } else {
        setShowPaymentModal(true);
      }
    }
  };

  const handleCardSubmit = (data, cardResponse, qrResponse) => {
    setShowCreateCardModal(false);

    // If API call was successful, you might want to store the card data or handle it differently
    if (cardResponse) {
      // You can store the card ID or other response data if needed
      localStorage.setItem(
        "patientCardId",
        cardResponse.id || cardResponse.cardId,
      );
    }

    // Store QR code data if generated successfully
    if (qrResponse) {
      localStorage.setItem("patientQRCode", JSON.stringify(qrResponse));
    }

    // After SAL card creation, start subscription flow instead of going
    // directly to the dashboard.
    navigate("/add-subscription");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5] py-5">
      <div className="w-11/12 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-xl p-10 border border-[#e5e7eb]">
        <h2 className="text-3xl font-extrabold text-[#2d348f] mb-8 text-center tracking-tight">
          {t("signupTitle", lang)}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <AccountInput
            label={t("signupName", lang)}
            name="name"
            value={form.name}
            onChange={handleChange}
          />
          <AccountInput
            label={t("signupEmail", lang)}
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
          />
          <AccountInput
            label={t("signupPassword", lang)}
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            showPasswordToggle={true}
          />
          <div className="flex flex-col mb-4 flex-1 min-w-[180px]">
            <label className="text-xs font-semibold text-[#837f90] mb-1">
              {t("signupRole", lang)}
            </label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
            >
              <option value="">{t("signupSelectRole", lang)}</option>
              <option value="doctor">{t("signupDoctor", lang)}</option>
              <option value="patient">{t("signupPatient", lang)}</option>
            </select>
          </div>

          <div className="flex items-start gap-2 mt-2">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#4962d8] border-gray-300 rounded focus:ring-[#4962d8]"
            />
            <label htmlFor="terms" className="text-sm text-[#837f90]">
              I agree to the{" "}
              <a
                href="/S.A.L.Mine_Website Terms of Use 08_05_2025 redline.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2d348f] font-semibold hover:underline"
              >
                Terms and Conditions
              </a>
            </label>
          </div>
          {error && <div className="text-red-500 text-sm">*{error}</div>}

          <AccountButton
            type="submit"
            className="mt-2 mb-1 rounded-lg shadow-md hover:shadow-lg transition-all"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              </div>
            ) : (
              t("signupButton", lang)
            )}
          </AccountButton>
        </form>
        <VerificationModal
          open={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onVerify={handleVerification}
          email={form.email}
          firstName={form?.name?.split(" ")[0] ?? ""}
          lang={lang}
        />
        <DashboardConfirmationModal
          open={showDashboardModal}
          firstName={form?.name?.split(" ")[0] ?? ""}
          onNavigate={handleDashboardModalClose}
          lang={lang}
        />
        <PaymentModal
          open={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPay={handlePayment}
        />
        <CreateCardModal
          open={showCreateCardModal}
          onClose={() => setShowCreateCardModal(false)}
          onSubmit={handleCardSubmit}
          lang={lang}
        />
        <div className="mt-6 text-center text-sm text-[#837f90]">
          {t("signupAlreadyAccount", lang)}{" "}
          <Link
            to="/login"
            className="text-[#2d348f] font-semibold hover:underline"
          >
            {t("signupLogin", lang)}
          </Link>
        </div>
      </div>
    </div>
  );
}
